<script lang="ts">
	//Hooks
	import { onMount } from 'svelte';
	//Formating
	import { format } from '../../../../services/i18n';
	//Services
	import { ChunkRequest, gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import { real_id } from '../../../../helpers/interface_helpers';
	import {
		capFirstChar,
		formatSnakeCaseToTitle,
		removeOrAddUnderscore,
		sortOn,
		transformedTimeZones,
	} from '../../../../helpers/utilities';
	import { onSettingsResponse, sleepingResponse } from '../../../../helpers/device_settings_helpers';
	import { getTimeZoneName, validateGeolocation } from '../../../../helpers/geolocalion_helper';
	import { getNumberInputConfig } from '../../../../helpers/number_input_helpers';
	import { coordinatesValidationHelper } from '../../../../helpers/coordinates_helpers';
	//Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Maps
	import SelectDeviceLocation from '../../../Maps/SelectDeviceLocation.svelte';
	//Design system components
	import { Alert, Button, Input, Loader, Select, Text, toast } from '@Shelly/shelly-design-system';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import type { Device } from '../../../../types/Device';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device: Device;

	const id = real_id(device.id);
	const settings = devices_settings.getByRealId(id);
	const loading = { detect: false, save: false };
	let timeZones = null;
	let timeZone = settings.sys.location?.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
	let longitude = settings.sys.location?.lon;
	let latitude = settings.sys.location?.lat;
	let timeZonesListError: string = null;
	let reqResTimezones: string[] = [];
	let chunkRequest = new ChunkRequest(device, 'Shelly.ListTimeZones', {}, false);
	let waitForResponse = false;
	let showModal = false;

	onMount(() => {
		if (!validateGeolocation(latitude, longitude)) {
			latitude = 0;
			longitude = 0;
		}
		getTimeZones();
		return () => {
			chunkRequest.abort();
		};
	});

	const getTimeZones = async () => {
		waitForResponse = true;
		timeZonesListError = null;
		reqResTimezones = [];

		try {
			const res: SettingRequest = await chunkRequest.get();

			if (chunkRequest.isAborted()) {
				return;
			}

			if (res?.isok) {
				reqResTimezones = res.data?.timezones || [];
				timeZones = transformedTimeZones(reqResTimezones);

				const isInList = timeZones.some((el) => el.stringToSend === timeZone);

				if (isInList) {
					timeZone = timeZones.find((el) => el.stringToSend === timeZone)?.stringToVisualise;
				} else {
					timeZones.push({
						stringToVisualise: removeOrAddUnderscore(timeZone, 'removeUnderscore'),
						stringToSend: removeOrAddUnderscore(timeZone, 'addUnderscore'),
					});

					timeZones = sortOn(timeZones, 'stringToSend');
					timeZone = timeZones.find((el) => el.stringToSend === timeZone)?.stringToVisualise;
				}
			}
			if (!timeZones) {
				timeZonesListError =
					sleepingResponse(res, device.type) ||
					$format('device_timezones_error', {
						default: 'Cloud not get device timezones.',
					});
			}
			waitForResponse = false;
		} catch (error) {
			console.error('error on getting timezones list', error);
		}
	};

	const saveLocationSettings = async () => {
		if (
			latitude >= 90 &&
			latitude.toString().includes('.') &&
			latitude.toString().split('.')[1].startsWith('00000')
		) {
			latitude = +latitude.toString().split('.')[0];
		}

		if (
			longitude >= 180 &&
			longitude.toString().includes('.') &&
			longitude.toString().split('.')[1].startsWith('00000')
		) {
			longitude = +longitude.toString().split('.')[0];
		}

		if (!validateGeolocation(latitude, longitude)) {
			toast.open({
				type: 'danger',
				title: $format('invalid_arguments', { default: 'Invalid arguments' }),
				text: $format('text_invalid_arguments', {
					default:
						"'Latitude' should be greater than -90 and less than 90, 'longitude' should be greater than -180 and less than 180!",
				}),
			});
			return;
		}

		loading.save = true;

		const tz = timeZones.find((el) => el.stringToVisualise === timeZone)?.stringToSend;

		const params = {
				config: {
					location: {
						tz,
						lat: Number(latitude),
						lon: Number(longitude),
					},
				},
			},
			res: SettingRequest = await gen2Request(device, 'Sys.SetConfig', params);
		if (res.isok) {
			settings.sys.location.tz = tz;
			settings.sys.location.lon = longitude;
			settings.sys.location.lat = latitude;
			devices_settings.update_devices_settings(id, settings);
		}

		onSettingsResponse(res, device, {
			success: $format('location_save_scc_toast', {
				default: 'Location settings saved successfully.',
			}),
			danger: $format('location_save_err_toast', {
				default: 'Failed to save location settings!',
			}),
		});

		loading.save = false;
	};

	const detectLocationSettings = async () => {
		loading.detect = true;
		const res: SettingRequest = await gen2Request(device, 'Shelly.DetectLocation', {}, false);

		if (res.isok) {
			const data = res.data || res;
			const rawTz = data['tz'];
			timeZone =
				timeZones.find((el) => el.stringToSend === rawTz)?.stringToVisualise ??
				removeOrAddUnderscore(rawTz, 'removeUnderscore');
			longitude = data['lon'];
			latitude = data['lat'];
			settings.sys.location.tz = rawTz;
			settings.sys.location.lat = latitude;
			settings.sys.location.lon = longitude;
			devices_settings.update_devices_settings(id, settings);
			toast.open({
				type: 'success',
				title: $format('location_detect_scc_toast', {
					default: 'Time zone auto detect completed successfully.',
				}),
			});
		} else {
			toast.open({
				type: 'danger',
				title: $format('location_detect_err_toast', {
					default: 'Failed to auto detect time zone!',
				}),
			});
		}
		loading.detect = false;
	};

	const onSelectMapLocation = async (e: CustomEvent) => {
		latitude = Number(e.detail.lat.toFixed(6));
		longitude = Number(e.detail.lng.toFixed(6));
		const newTimeZone = await getTimeZoneName(e.detail.lat, e.detail.lng);
		if (reqResTimezones.includes(newTimeZone)) {
			timeZone =
				timeZones.find((el) => el.stringToSend === newTimeZone)?.stringToVisualise ??
				removeOrAddUnderscore(newTimeZone, 'removeUnderscore');
		}
	};

	$: {
		if (latitude) latitude = coordinatesValidationHelper(latitude.toString());
		if (longitude) longitude = coordinatesValidationHelper(longitude.toString());
	}

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && timeZones !== null && !waitForResponse && !timeZonesListError) {
			stickyBottomAction.set({
				onClick: saveLocationSettings,
				loading: loading.save,
				disabled: loading.detect,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if waitForResponse}
	<Loader size="l" class="d-flex justify-content-center mb-s" />
{:else if timeZonesListError}
	<Alert type="danger" heading={timeZonesListError} class="mb-s" />
	<Button
		size="m"
		class="width-full mr-s text-capitalized"
		responsiveSize={{
			sm: 'm',
		}}
		variant="primary"
		on:click={getTimeZones}
	>
		{$format('button_rescan', { default: 'Rescan' })}
	</Button>
{:else if timeZones !== null}
	<div class="location-g2-settings">
		<Button
			size="m"
			width="full"
			variant="secondary"
			disabled={loading.save || loading.detect}
			on:click={() => {
				if (
					latitude >= 90 &&
					latitude.toString().includes('.') &&
					latitude.toString().split('.')[1].startsWith('00000')
				) {
					latitude = +latitude.toString().split('.')[0];
				}

				if (
					longitude >= 180 &&
					longitude.toString().includes('.') &&
					longitude.toString().split('.')[1].startsWith('00000')
				) {
					longitude = +longitude.toString().split('.')[0];
				}

				if (!validateGeolocation(latitude, longitude)) {
					latitude = settings.sys.location?.lat;
					longitude = settings.sys.location?.lon;
				}
				showModal = true;
			}}
		>
			{capFirstChar($format('select_map_location'))}
		</Button>
		<Button
			size="m"
			loading={loading.detect}
			width="full"
			variant="secondary"
			disabled={loading.save}
			on:click={detectLocationSettings}
		>
			{capFirstChar(
				$format('button_location_auto_detect', {
					default: 'auto detect location',
				}),
			)}
		</Button>
		{#if showModal}
			<SelectDeviceLocation
				bind:showModal
				lat={latitude}
				lng={longitude}
				on:change={onSelectMapLocation}
			/>
		{/if}

		<div class="location-g2-settings-group">
			<Text styleType="body-small">
				{formatSnakeCaseToTitle($format('location_time_zone', { default: 'time zone' }))}:
			</Text>
			<Select
				size="m"
				value={timeZone}
				isAutocomplete
				icon="fas fa-globe"
				options={timeZones.map((t) => t.stringToVisualise)}
				on:change={(e) => (timeZone = e.detail)}
				id="select-4ss5ruf"
				aria-label={$format('location_time_zone', { default: 'time zone' })}
			/>
		</div>

		<div class="location-g2-settings-group">
			<Text styleType="body-small">
				{formatSnakeCaseToTitle($format('location_latitude', { default: 'latitude' }))}:
			</Text>
			<Input
				{...getNumberInputConfig()}
				size="m"
				bind:value={latitude}
				min={-90}
				max={90}
				step={0.000001}
				id="input-pjci61n"
				aria-label={$format('location_latitude', { default: 'latitude' })}
			/>
		</div>

		<div class="location-g2-settings-group">
			<Text styleType="body-small">
				{formatSnakeCaseToTitle($format('location_longtitude', { default: 'longitude' }))}:
			</Text>
			<Input
				{...getNumberInputConfig()}
				size="m"
				bind:value={longitude}
				min={-180}
				max={180}
				step={0.000001}
				id="input-jfha90p"
				aria-label={$format('location_longtitude', { default: 'longitude' })}
			/>
		</div>
	</div>
	{#if !$showNewLayout}
		<Button
			class="mt-m"
			size="m"
			loading={loading.save}
			width="full"
			disabled={loading.detect}
			on:click={saveLocationSettings}
		>
			{$format('button_save')}
		</Button>
	{/if}
{/if}

<style>
	.location-g2-settings {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 12px;
		width: 100%;
	}
	.location-g2-settings-group {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
		width: 100%;
	}
</style>
